/* =============================================================================
   helpers.js  —  client-side utilities. No external API keys, no trackers.
   .ics + Google Calendar + directions + CSV export + QR (via qrcode-generator).
============================================================================= */
import qrcode from 'qrcode-generator';

/* parse "11:00 AM" -> { h, m } 24h */
function parseTime(t) {
  if (!t) return { h: 11, m: 0 };
  const m = String(t).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!m) return { h: 11, m: 0 };
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = (m[3] || '').toLowerCase();
  if (ap.startsWith('p') && h < 12) h += 12;
  if (ap.startsWith('a') && h === 12) h = 0;
  return { h, m: min };
}

/* "2026-08-08" + time -> Date (local) */
export function eventDate(dateISO, time) {
  const [y, mo, d] = (dateISO || '2026-08-08').split('-').map(Number);
  const { h, m } = parseTime(time);
  return new Date(y, (mo || 1) - 1, d || 1, h, m, 0);
}

function pad(n) {
  return String(n).padStart(2, '0');
}
function fmtCal(dt) {
  return (
    dt.getFullYear() +
    pad(dt.getMonth() + 1) +
    pad(dt.getDate()) +
    'T' +
    pad(dt.getHours()) +
    pad(dt.getMinutes()) +
    '00'
  );
}

function calStartEnd(event) {
  const start = eventDate(event.dateISO, event.time);
  let end = eventDate(event.dateISO, event.endTime);
  if (!(end > start)) end = new Date(start.getTime() + 3 * 3600 * 1000);
  return { start, end };
}

export function downloadICS(event) {
  const { start, end } = calStartEnd(event);
  const loc = [event.venueName, event.venueAddress].filter(Boolean).join(', ');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Our Little Love Bug//Baby Shower//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    'UID:' + Date.now() + '@littlelovebug',
    'DTSTAMP:' + fmtCal(new Date()),
    'DTSTART:' + fmtCal(start),
    'DTEND:' + fmtCal(end),
    'SUMMARY:' + escapeICS(event.headline + ' — a baby shower'),
    loc ? 'LOCATION:' + escapeICS(loc) : '',
    'DESCRIPTION:' + escapeICS(event.subline),
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  // VEVENT SUMMARY may include an em dash from the join above; strip for user-facing files.
  const ics = lines.join('\r\n').replace(/—/g, '-');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  triggerDownload(blob, 'our-little-love-bug.ics');
}
function escapeICS(s) {
  return String(s || '')
    .replace(/[\\;,]/g, (c) => '\\' + c)
    .replace(/\n/g, '\\n');
}

export function googleCalUrl(event) {
  const { start, end } = calStartEnd(event);
  const loc = [event.venueName, event.venueAddress].filter(Boolean).join(', ');
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.headline + ': a baby shower',
    dates: fmtCal(start) + '/' + fmtCal(end),
    details: event.subline,
    location: loc,
  });
  return 'https://calendar.google.com/calendar/render?' + p.toString();
}

export function directionsUrl(event) {
  const dest = [event.venueName, event.venueAddress].filter(Boolean).join(', ');
  return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
}

/* ---------------------------------- CSV ---------------------------------- */
export function toCSV(rows, headers) {
  const esc = (v) => {
    const s = v == null || v === '' ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const head = headers.map((h) => esc(h.label)).join(',');
  const body = rows
    .map((r) => headers.map((h) => esc(typeof h.get === 'function' ? h.get(r) : r[h.key])).join(','))
    .join('\n');
  return head + '\n' + body;
}
export function downloadCSV(filename, rows, headers) {
  const blob = new Blob(['﻿' + toCSV(rows, headers)], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/* ---------------------------------- QR ----------------------------------- */
export function qrSvg(text, cell = 5) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return qr.createSvgTag({ cellSize: cell, margin: 2, scalable: true });
}

/* Ensure a user-entered link is absolute. Without a protocol the browser
   treats "my.babylist.com/x" as a path relative to the current page. */
export function normalizeUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^mailto:|^tel:/i.test(s)) return s;
  return 'https://' + s.replace(/^\/+/, '');
}

export function fmtDateTime(ts) {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
